import {
  ValidationErrors,
  Validators,
  AbstractControl,
  ValidatorFn,
} from '@angular/forms';
import { isAfter, isBefore, isValid } from 'date-fns';

import { specialCharactersRegex } from '@digital-platform/shared/domain';
import { PasswordRequirements } from '@digital-platform/users/domain';

export class FormValidators {
  static requiredIf<T>(predicate: () => boolean): ValidatorFn {
    const validatorFn = (control: AbstractControl<T>) => {
      try {
        if (predicate()) {
          return Validators.required(control);
        }
      } catch {
        // ignore any errors from attempting to call the predicate
        return null;
      }

      return null;
    };

    return validatorFn as ValidatorFn;
  }

  static requiredTrueIf<T>(predicate: () => boolean): ValidatorFn {
    const validatorfn = (control: AbstractControl<T>) => {
      try {
        if (predicate()) {
          return Validators.requiredTrue(control);
        }
      } catch {
        // ignore any errors from attempting to call the predicate
        return null;
      }

      return null;
    };

    return validatorfn as ValidatorFn;
  }

  static greaterThan<T extends string>(predicate: () => number): ValidatorFn {
    const validatorFn = (control: AbstractControl<T>) => {
      try {
        const greaterThan = predicate();
        const value = parseFloat(control.value);

        // only validate if we have a value to compare against and the control value parses properly
        return !!greaterThan && !isNaN(value) && value <= greaterThan
          ? { greaterThan: { greaterThan, actual: control.value } }
          : null;
      } catch {
        // ignore any errors from attempting to call the predicate
        return null;
      }
    };

    return validatorFn as ValidatorFn;
  }

  static lessThan<T extends string>(predicate: () => number): ValidatorFn {
    const validatorFn = (control: AbstractControl<T>) => {
      try {
        const lessThan = predicate();
        const value = parseFloat(control.value);

        // only validate if we have a value to compare against and the control value parses properly
        return !!lessThan && !isNaN(value) && value >= lessThan
          ? { lessThan: { lessThan, actual: control.value } }
          : null;
      } catch {
        // ignore any errors from attempting to call the predicate
        return null;
      }
    };

    return validatorFn as ValidatorFn;
  }

  static integer<T extends string>(
    control: AbstractControl<T>
  ): ValidationErrors | null {
    return Validators.pattern(/^(0|-?[1-9][0-9]*)$/)(control)
      ? { integer: true }
      : null;
  }

  static decimal<T extends string>(places?: number): ValidatorFn {
    const validatorFn = (control: AbstractControl<T>) => {
      let pattern = /^(0|-?[1-9][0-9]*)(\.[0-9]+)?$/;

      if (places) {
        pattern = new RegExp(`^(0|-?[1-9][0-9]*)(\\\.[0-9]{1,${places}})?$`);
      }
      return Validators.pattern(pattern)(control) ? { decimal: true } : null;
    };

    return validatorFn as ValidatorFn;
  }

  static beforeDate<T extends Date>(predicate: () => Date): ValidatorFn {
    const validatorFn = (control: AbstractControl<T>) => {
      try {
        const beforeDate = predicate();
        const value = control.value;

        // only validate if we have a value to compare against and the control value parses properly
        return !!beforeDate && isValid(value) && !isBefore(value, beforeDate)
          ? { beforeDate: { beforeDate, actual: control.value } }
          : null;
      } catch {
        // ignore any errors from attempting to call the predicate
        return null;
      }
    };

    return validatorFn as ValidatorFn;
  }

  static afterDate<T extends Date>(predicate: () => Date): ValidatorFn {
    const validatorFn = (control: AbstractControl<T>) => {
      try {
        const afterDate = predicate();
        const value = control.value;

        // only validate if we have a value to compare against and the control value parses properly
        return !!afterDate && isValid(value) && !isAfter(value, afterDate)
          ? { afterDate: { afterDate, actual: control.value } }
          : null;
      } catch {
        // ignore any errors from attempting to call the predicate
        return null;
      }
    };

    return validatorFn as ValidatorFn;
  }

  static subsetOfRegexes<T extends string>(
    patterns: { [key: string]: RegExp },
    howMany: number
  ): ValidatorFn {
    const validatorFn = (control: AbstractControl<T>) => {
      if (control.value) {
        const met = [];
        const unmet = [];
        Object.entries(patterns).forEach(([key, pattern]) => {
          if (pattern.test(control.value)) {
            met.push(key);
          } else {
            unmet.push(key);
          }
        });

        if (met.length < howMany) {
          return {
            subsetOfRegexes: {
              unmet,
              met,
              mustBeMet: howMany,
            },
          };
        }
      }

      return null;
    };

    return validatorFn as ValidatorFn;
  }

  static passwordRequirements<T extends string>(
    requirements: PasswordRequirements
  ): ValidatorFn {
    const patterns = requirements.characterTypes.reduce(
      (memo, type) => ({ ...memo, [type.type]: type.pattern }),
      {}
    );

    const validator = Validators.compose([
      Validators.minLength(requirements.minLength),
      FormValidators.subsetOfRegexes<T>(
        patterns,
        requirements.minCharacterTypes
      ),
    ]);

    const validatorfn = (control: AbstractControl<T>) => {
      let result = validator(control);

      if (result?.subsetOfRegexes) {
        const { subsetOfRegexes, ...rest } = result;

        const missing = subsetOfRegexes.unmet.map(
          (type: string) =>
            requirements.characterTypes.find((t) => t.type === type)?.label
        );

        result = {
          ...rest,
          characterTypes: {
            min: requirements.minCharacterTypes,
            total: requirements.characterTypes.length,
            missing,
          },
        };
      }

      if (result !== null) {
        result = {
          passwordRequirements: result,
        };
      }

      return result;
    };

    return validatorfn as ValidatorFn;
  }
  // TODO: refactor this to use a proper return type.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static specialCharacters(control: AbstractControl<string>): any {
    if (control.value && specialCharactersRegex.test(control.value)) {
      return { specialCharacters: true };
    }
  }
}
